import React from 'react';
import PropTypes from 'prop-types';
import { is, List } from 'immutable';
import moment from 'moment';
import { omit, intersection } from 'lodash';
import { compose, lifecycle, withProps, mapProps, branch, withHandlers } from 'recompose';

import {
  getDatesInWeekWithDate,
  getWeekIdentifier,
} from '../../../lib/dates';
import styles from './timeplan.css';
import getTimeplanUnitsByDate from './getTimeplanUnitsByDate';
import findOverlapping from './findOverlapping';
import findDuplicates from './findDuplicates';
import UnselectedDate from './UnselectedDate';
import SelectedDate from './SelectedDate';
import SelectedDateV2 from './v2/SelectedDate';

const DefaultHeader = compose(
  withProps(({ weekIdentifier }) => {
    const weekDateMoment = moment(weekIdentifier).endOf('isoWeek');
    const weekNumber = weekDateMoment.isoWeek();
    const weekYear = weekDateMoment.year();
    return {
      weekNumber,
      weekYear,
    };
  }),
)(({ weekNumber, weekYear }) => (
  <div className={styles.weekHeaderContainer}>
    <div className={styles.weekHeader}>
      <span className={styles.weekNumberText}>v. {weekNumber}</span>
      <span className={styles.yearText}> {weekYear}</span>
    </div>
  </div>
));

const HOCSelectedDate = compose(
  mapProps(({
    date,
    timeplanUnits,
    mileageAllowances,
    compareTimeplanUnits,
    bookedTimeplanUnits,
    resourceReportedTimeplanUnits,
    reportedTimeplanUnits,
    ...props
  }) => ({
    ...props,
    date,
    timeplanUnits: getTimeplanUnitsByDate(timeplanUnits, date),
    mileageAllowances: getTimeplanUnitsByDate(mileageAllowances, date),
    allMileageAllowances: mileageAllowances,
    compareTimeplanUnits: compareTimeplanUnits ?
      getTimeplanUnitsByDate(compareTimeplanUnits, date) : new List(),
    bookedTimeplanUnits: bookedTimeplanUnits ?
      bookedTimeplanUnits.filter(tpu => tpu.date === date) : undefined,
    resourceReportedTimeplanUnits: resourceReportedTimeplanUnits ?
      resourceReportedTimeplanUnits.filter(tpu => tpu.date === date) : undefined,
    reportedTimeplanUnits: reportedTimeplanUnits ?
      reportedTimeplanUnits.filter(tpu => tpu.date === date) : undefined,
    duplicates: findDuplicates(timeplanUnits.toJS()),
  })),
  withProps(({ timeplanUnits, duplicates }) => ({
    overlaps: omit(findOverlapping(timeplanUnits.toJS()), Object.keys(duplicates)),
  })),
  branch(({ version }) => version > 1,
    withProps({
      SelectedDateComponent: SelectedDateV2,
    }),
    withProps({
      SelectedDateComponent: SelectedDate,
    }),
  ),
)(({ SelectedDateComponent, ...props }) => (
  <SelectedDateComponent
    {...props}
  />
));

const TimeplanWeekBody = compose(
  withProps(({ weekIdentifier, selectedDates, strictDates }) => ({
    weekDates: strictDates ?
      intersection(getDatesInWeekWithDate(weekIdentifier), selectedDates.toJS()) :
      getDatesInWeekWithDate(weekIdentifier),
  })),
  withHandlers({
    switchIndicate: ({
      allTimeplanUnits,
      timeplanUnits,
      onChangeTimeplanUnits,
      weekDates,
    }) => () => {
      const hasIndicateHandle = !!timeplanUnits.find(({ indicateHandle }) => !!indicateHandle);
      const newTimeplanUnits = allTimeplanUnits
        .map((tpu) => {
          if (weekDates.includes(tpu.date)) {
            return tpu.set('indicateHandle', !hasIndicateHandle);
          }
          return tpu;
        });
      const transformFunc = (o, tpus) => tpus;
      onChangeTimeplanUnits(transformFunc, newTimeplanUnits);
    },
  }),
)(({
  weekDates,
  selectedDates,
  hideInactiveDates,
  displayWeekNumber,
  useIndicate,
  switchIndicate,
  ...props
}) => (
  <React.Fragment>
    {displayWeekNumber && (
      <div className="row">
        <div className="col-md-1">
          <b
            onClick={useIndicate ? switchIndicate : undefined}
            role="button"
            tabIndex={useIndicate ? 0 : undefined}
          >
            V.{moment(weekDates[0]).format('WW')}
          </b>
        </div>
      </div>
    )}
    <div className={`row ${styles.dateRow}`}>
      {
        weekDates.map((date) => {
          const isDateSelected = selectedDates.includes(date);
          if (!isDateSelected) {
            if (hideInactiveDates) {
              return (
                <div key={date} className={`col-md-1 ${styles.sevencols}`} />
              );
            }

            return (
              <UnselectedDate
                key={date}
                date={date}
              />
            );
          }

          return (<HOCSelectedDate
            key={date}
            selectedDates={selectedDates}
            date={date}
            useIndicate={useIndicate}
            {...props}
          />);
        })
      }
    </div>
  </React.Fragment>
));

function TimeplanWeek({
  weekIdentifier,
  showWeekHeader,
  renderWeekHeader,
  renderWeekFooter,
  noPanel,
  ...props
}) {
  if (noPanel) {
    return (
      <TimeplanWeekBody weekIdentifier={weekIdentifier} {...props} />
    );
  }

  return (
    <div className="panel panel-default">
      {(showWeekHeader || renderWeekHeader) && (
        <div className="panel-heading">
          {showWeekHeader && (
            <DefaultHeader weekIdentifier={weekIdentifier} />
          )}
          {renderWeekHeader && renderWeekHeader()}
        </div>
      )}
      <div className="panel-body">
        <TimeplanWeekBody weekIdentifier={weekIdentifier} {...props} />
      </div>
      {renderWeekFooter && (
        <div className="panel-footer">
          {renderWeekFooter()}
        </div>
      )}
    </div>
  );
}

TimeplanWeek.defaultProps = {
  showWeekHeader: true,
  renderWeekHeader: undefined,
  renderWeekFooter: undefined,
  noPanel: false,
  useIndicate: false,
};

TimeplanWeek.propTypes = {
  weekIdentifier: PropTypes.string.isRequired,
  showWeekHeader: PropTypes.bool,
  renderWeekHeader: PropTypes.func,
  renderWeekFooter: PropTypes.func,
  noPanel: PropTypes.bool,
  useIndicate: PropTypes.bool,
};

export default compose(
  lifecycle({
    shouldComponentUpdate(nextProps) {
      if (
        this.props.weekIdentifier !== nextProps.weekIdentifier
        || !is(this.props.resources, nextProps.resources)
        || !is(this.props.selectedDates, nextProps.selectedDates)
        || !is(this.props.workTypes, nextProps.workTypes)
        || !is(this.props.bookedTimeplanUnits, nextProps.bookedTimeplanUnits)
        || !is(this.props.resourceReportedTimeplanUnits, nextProps.resourceReportedTimeplanUnits)
        || !is(this.props.reportedTimeplanUnits, nextProps.reportedTimeplanUnits)
        || !is(this.props.absentReasons, nextProps.absentReasons)
        || !is(this.props.dayComments, nextProps.dayComments)
        || !is(this.props.allTimeplanUnits, nextProps.allTimeplanUnits) // Used for indicateHandle
        || !is(this.props.mileageAllowances, nextProps.mileageAllowances) // Used for indicateHandle
        || !is(
          this.props.timeplanUnits.filter(
            t => getWeekIdentifier(t.get('date')) === this.props.weekIdentifier),
          nextProps.timeplanUnits.filter(
            t => getWeekIdentifier(t.get('date')) === this.props.weekIdentifier),
        )
        || this.props.isEditable !== nextProps.isEditable
        || this.props.currentMode !== nextProps.currentMode
      ) {
        return true;
      }

      return false;
    },
  }),
)(TimeplanWeek);
